// QA API service with optional device authentication integration
import type { 
  QAApiRequest, 
  QAApiResponse, 
  QAApiError, 
  QAServiceConfig, 
  QAServiceResponse,
  QAApiSource,
  MessageSource,
  EnhancedQAApiResponse,
  SuggestionSelectionRequest,
  SuggestionSelectionResponse
} from '../types/chat';

// Export the error type for use in other components
export type { QAApiError };

class QAService {
  private config: QAServiceConfig;
  private authService?: unknown; // Optional for now

  constructor(authService?: unknown) {
    this.authService = authService;
    this.config = {
      baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787',
      timeout: 30000, // 30 seconds
      retryAttempts: 3,
      headers: {}
    };
  }

  // Convert QA API source to MessageSource format
  private convertQASourceToMessageSource = (source: QAApiSource): MessageSource => {
    return {
      id: source.id,
      title: source.title,
      url: source.url || undefined, // Handle optional url
      excerpt: source.excerpt,
      confidence: source.confidence
    };
  };

  // Make API request with retry logic (authentication disabled for now)
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<QAServiceResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      // Get API key from auth service if available
      // const apiKey = this.authService?.getApiKey();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...this.config.headers,
        ...(options.headers as Record<string, string> || {})
      };

      // Add authorization header if API key is available
      // if (apiKey) {
      //   headers['Authorization'] = `Bearer ${apiKey}`;
      // }

      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: {
            status_code: response.status,
            message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
            details: errorData
          }
        };
      }

      const responseData = await response.json();
      
      // Handle API responses that have a nested structure with success/data
      if (responseData.success && responseData.data) {
        return {
          success: true,
          data: responseData.data
        };
      }
      
      // Handle direct data responses
      return {
        success: true,
        data: responseData
      };

    } catch (error: unknown) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            error: {
              error: 'Request timeout',
              status_code: 408,
              message: 'Request timeout'
            }
          };
        }

        return {
          success: false,
          error: {
            error: 'Network error',
            status_code: 0,
            message: error.message || 'Network error'
          }
        };
      }

      return {
        success: false,
        error: {
          error: 'Unknown error',
          status_code: 0,
          message: 'Unknown error occurred'
        }
      };
    }
  }

  // Ask a question to the enhanced QA API
  async askQuestionEnhanced(
    question: string,
    options: {
      context?: string;
      language?: 'vi' | 'en';
      maxResults?: number;
    } = {}
  ): Promise<QAServiceResponse<EnhancedQAApiResponse>> {
    // Validate input
    if (!question || question.trim().length === 0) {
      return {
        success: false,
        error: {
          error: 'Empty question',
          status_code: 400,
          message: 'Question cannot be empty'
        }
      };
    }

    if (question.length > 1000) {
      return {
        success: false,
        error: {
          error: 'Question too long',
          status_code: 400,
          message: 'Question is too long (max 1000 characters)'
        }
      };
    }

    const requestBody: QAApiRequest = {
      question: question.trim(),
      context: options.context,
      language: options.language || 'vi',
      max_results: options.maxResults || 5
    };

    let attempt = 0;
    const maxRetries = this.config.retryAttempts;

    while (attempt <= maxRetries) {
      const response = await this.makeRequest<EnhancedQAApiResponse>('/api/ask', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.success) {
        // Validate response structure
        if (!response.data || typeof response.data.question_id !== 'string') {
          return {
            success: false,
            error: {
              error: 'INVALID_RESPONSE',
              status_code: 500,
              message: 'Invalid response format from server'
            }
          };
        }

        return response;
      }

      // Check if we should retry
      const shouldRetry = response.error && 
        (response.error.status_code === 429 || // Rate limit
         response.error.status_code === 500 || // Server error
         response.error.status_code === 502 || // Bad gateway
         response.error.status_code === 503 || // Service unavailable
         response.error.status_code === 504);  // Gateway timeout

      if (!shouldRetry || attempt >= maxRetries) {
        return response;
      }

      attempt++;
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    return {
      success: false,
      error: {
        error: 'MAX_RETRIES_EXCEEDED',
        status_code: 500,
        message: `Failed after ${maxRetries + 1} attempts`
      }
    };
  }

  // Select a suggestion for detailed answer
  async selectSuggestion(
    request: SuggestionSelectionRequest
  ): Promise<QAServiceResponse<SuggestionSelectionResponse>> {
    // Validate input
    if (!request.question_id || !request.suggestion_id || !request.selected_suggestion) {
      return {
        success: false,
        error: {
          status_code: 400,
          message: 'Missing required fields for suggestion selection'
        }
      };
    }

    let attempt = 0;
    const maxRetries = this.config.retryAttempts;

    while (attempt <= maxRetries) {
      const response = await this.makeRequest<SuggestionSelectionResponse>('/api/ask/select', {
        method: 'POST',
        body: JSON.stringify(request)
      });

      if (response.success) {
        // Validate response structure
        if (!response.data || typeof response.data.answer !== 'string') {
          return {
            success: false,
            error: {
              status_code: 500,
              message: 'Invalid response format from server'
            }
          };
        }

        return response;
      }

      // Check if we should retry
      const shouldRetry = response.error && 
        (response.error.status_code === 429 || // Rate limit
         response.error.status_code === 500 || // Server error
         response.error.status_code === 502 || // Bad gateway
         response.error.status_code === 503 || // Service unavailable
         response.error.status_code === 504);  // Gateway timeout

      if (!shouldRetry || attempt >= maxRetries) {
        return response;
      }

      attempt++;
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    return {
      success: false,
      error: {
        status_code: 500,
        message: `Failed after ${maxRetries + 1} attempts`
      }
    };
  }

  // Ask an exact question (for suggestion clicks)
  async askExactQuestion(
    question: string,
    options: {
      context?: string;
      language?: 'vi' | 'en';
      maxResults?: number;
    } = {}
  ): Promise<QAServiceResponse<EnhancedQAApiResponse>> {
    // Validate input
    if (!question || question.trim().length === 0) {
      return {
        success: false,
        error: {
          status_code: 400,
          message: 'Question cannot be empty'
        }
      };
    }

    if (question.length > 1000) {
      return {
        success: false,
        error: {
          status_code: 400,
          message: 'Question is too long (max 1000 characters)'
        }
      };
    }

    const requestBody: QAApiRequest = {
      question: question.trim(),
      context: options.context,
      language: options.language || 'vi',
      max_results: options.maxResults || 5
    };

    let attempt = 0;
    const maxRetries = this.config.retryAttempts;

    while (attempt <= maxRetries) {
      // Add exact=true query parameter for exact queries
      const response = await this.makeRequest<EnhancedQAApiResponse>('/api/ask?exact=true', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.success) {
        // Validate response structure
        if (!response.data || typeof response.data.question_id !== 'string') {
          return {
            success: false,
            error: {
              error: 'INVALID_RESPONSE',
              status_code: 500,
              message: 'Invalid response format from server'
            }
          };
        }

        return response;
      }

      // Check if we should retry
      const shouldRetry = response.error && 
        (response.error.status_code === 429 || // Rate limit
         response.error.status_code === 500 || // Server error
         response.error.status_code === 502 || // Bad gateway
         response.error.status_code === 503 || // Service unavailable
         response.error.status_code === 504);  // Gateway timeout

      if (!shouldRetry || attempt >= maxRetries) {
        return response;
      }

      attempt++;
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    return {
      success: false,
      error: {
        error: 'Max retries exceeded',
        status_code: 500,
        message: `Failed after ${maxRetries + 1} attempts`
      }
    };
  }

  // Ask a question to the QA API (legacy method)
  async askQuestion(
    question: string,
    options: {
      context?: string;
      language?: 'vi' | 'en';
      maxResults?: number;
    } = {}
  ): Promise<QAServiceResponse<QAApiResponse>> {
    // Validate input
    if (!question || question.trim().length === 0) {
      return {
        success: false,
        error: {
          status_code: 400,
          message: 'Question cannot be empty'
        }
      };
    }

    if (question.length > 1000) {
      return {
        success: false,
        error: {
          status_code: 400,
          message: 'Question is too long (max 1000 characters)'
        }
      };
    }

    // Check authentication if auth service is available
    // if (this.authService && !this.authService.isAuthenticated()) {
    //   return {
    //     success: false,
    //     error: {
    //       status_code: 401,
    //       message: 'Authentication required'
    //     }
    //   };
    // }

    const requestBody: QAApiRequest = {
      question: question.trim(),
      context: options.context,
      language: options.language || 'vi',
      max_results: options.maxResults || 5
    };

    let attempt = 0;
    const maxRetries = this.config.retryAttempts;

    while (attempt <= maxRetries) {
      const response = await this.makeRequest<QAApiResponse>('/api/ask', {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });

      if (response.success) {
        // Validate response structure
        if (!response.data || typeof response.data.answer !== 'string') {
          return {
            success: false,
            error: {
              error: 'Invalid response format',
              status_code: 500,
              message: 'Invalid response format from server'
            }
          };
        }

        // Convert sources to MessageSource format
        const sources = response.data.sources?.map(this.convertQASourceToMessageSource) || [];

        return {
          success: true,
          data: {
            ...response.data,
            sources
          }
        };
      }

      // Check if we should retry
      const shouldRetry = response.error && 
        (response.error.status_code === 429 || // Rate limit
         response.error.status_code === 500 || // Server error
         response.error.status_code === 502 || // Bad gateway
         response.error.status_code === 503 || // Service unavailable
         response.error.status_code === 504);  // Gateway timeout

      if (!shouldRetry || attempt >= maxRetries) {
        return response;
      }

      attempt++;
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, attempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    return {
      success: false,
      error: {
        status_code: 500,
        message: `Failed after ${maxRetries + 1} attempts`
      }
    };
  }

  // Check service health
  async checkHealth(): Promise<{ healthy: boolean; message: string }> {
    try {
      // Authentication check disabled for now
      // if (this.authService && !this.authService.isAuthenticated()) {
      //   return {
      //     healthy: false,
      //     message: 'Device not authenticated'
      //   };
      // }

      // Simple health check with a minimal request
      const response = await this.makeRequest<QAApiResponse>('/api/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: 'health check',
          language: 'en',
          max_results: 1
        })
      });

      return {
        healthy: response.success,
        message: response.success ? 'QA service is healthy' : response.error?.message || 'Service unavailable'
      };

    } catch (error) {
      return {
        healthy: false,
        message: error instanceof Error ? error.message : 'Health check failed'
      };
    }
  }

  // Update service configuration
  updateConfig(newConfig: Partial<QAServiceConfig>): void {
    this.config = {
      ...this.config,
      ...newConfig
    };
  }

  // Get current configuration
  getConfig(): QAServiceConfig {
    return { ...this.config };
  }
}

// Create singleton instance
let qaServiceInstance: QAService | null = null;

export const createQAService = (authService?: unknown): QAService => {
  if (!qaServiceInstance) {
    qaServiceInstance = new QAService(authService);
  }
  return qaServiceInstance;
};

export const getQAService = (): QAService | null => {
  return qaServiceInstance;
};

export { QAService };
export default QAService;